/**
 * Data → Reps (2026-10-16, Helena: "the rep home address — add that under Data, so all the master data that needs
 * updating is in one section"; "make a rep section under Data and put Add rep there"). Every rep of the organisation:
 * home address (the route starts and ends there), stores per day, stores per retailer, phone login. Edit home /
 * Add rep re-plan the rep. Org admin (Linda) and masters edit, a CAM views. Rep logins are made on Users → Reps.
 */
import { useCallback, useEffect, useMemo, useState } from "react"
import axios from "axios"
import {
  Box, Button, Flex, HStack, Input, Text, Badge, Spinner, Table, Tbody, Td, Th, Thead, Tr, useToast,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton, NumberInput, NumberInputField,
} from "@chakra-ui/react"
import { FaHome, FaUserPlus } from "react-icons/fa"
import PageToolbar from "../../../components/shared/PageToolbar"
import { Panel } from "../../../components/shared/Section"
import { useSupplierRetailer } from "../../../wrappers/SupplierRetailerContext"
import { neutral, type } from "../../../theme/tokens"

const API = process.env.REACT_APP_API_URL
const uid = () => localStorage.getItem("userid")

const RepReps = () => {
  const { selectedOrg, isMaster } = useSupplierRetailer()
  const toast = useToast()
  const orgArg = useMemo(() => (isMaster ? { org_code: selectedOrg } : {}), [isMaster, selectedOrg])
  const post = useCallback((path, body = {}) => axios.post(`${API}/field/${path}`, { userid: uid(), ...orgArg, ...body }), [orgArg])

  const [list, setList] = useState(null)
  const [canEdit, setCanEdit] = useState(false)
  const [error, setError] = useState(null)
  const [q, setQ] = useState("")
  const [busy, setBusy] = useState(false)
  const [edit, setEdit] = useState(null) // {mode: 'home' | 'add', rep_name, home_label, lat, lng, stores_per_day}

  const load = useCallback(() => {
    setList(null); setError(null)
    post("reps").then((r) => { setList(r.data.data || []); setCanEdit(!!r.data.canEdit) }).catch((e) => setError(e?.response?.data?.message || e.message))
  }, [post])
  useEffect(load, [load])

  const save = async () => {
    setBusy(true)
    try {
      const coords = edit.lat !== "" && edit.lng !== "" ? { lat: Number(edit.lat), lng: Number(edit.lng) } : {}
      if (edit.mode === "add") {
        const r = await post("rep-add", { rep_name: edit.rep_name, home_label: edit.home_label, stores_per_day: edit.stores_per_day, email: edit.email, cell: edit.cell, ...coords })
        toast({ title: `${r.data.data.rep_name} added`, description: "Give the rep stores on Data → Stores. The phone login is made on Users → Reps.", status: "success", duration: 6000, isClosable: true })
      } else {
        await post("rep-contact", { rep: edit.rep_name, email: edit.email, cell: edit.cell })
        // the home / stores per day only when they changed (saving them re-plans the rep)
        if (edit.home_label !== edit.home0 || coords.lat != null || Number(edit.stores_per_day) !== Number(edit.spd0)) {
          const r = await post("home", { rep: edit.rep_name, label: edit.home_label, stores_per_day: edit.stores_per_day, ...coords })
          const p = r.data.data.plan || {}
          toast({ title: "Saved and route re-planned", description: `${(p.planned || 0) + (p.repeats || 0)} visits to month end`, status: "success", isClosable: true })
        } else toast({ title: "Contact details saved", status: "success", duration: 2500, isClosable: true })
      }
      setEdit(null); load()
    } catch (e) {
      toast({ title: "Not saved", description: e?.response?.data?.message || e.message, status: "error", isClosable: true })
    } finally { setBusy(false) }
  }

  if (error) return <Panel><Text fontSize={type.size.small} color={neutral[600]}>{error}</Text></Panel>
  const t = q.trim().toLowerCase()
  const rows = (list || []).filter((r) => !t || `${r.rep_name} ${r.home_label || ""} ${r.email || ""} ${r.cell || ""}`.toLowerCase().includes(t))

  return (
    <Box>
      <PageToolbar
        description={`Every rep — home address (the route starts and ends there), stores per day and stores per retailer${canEdit ? "" : " · view only"}`}
        actions={canEdit ? <Button size="sm" colorScheme="teal" variant="outline" leftIcon={<FaUserPlus />} onClick={() => setEdit({ mode: "add", rep_name: "", home_label: "", lat: "", lng: "", stores_per_day: 6, email: "", cell: "" })}>Add rep</Button> : null}
      />
      <Panel>
        <Flex mb={3}><Input size="sm" maxW="300px" placeholder="Search rep or home address" value={q} onChange={(e) => setQ(e.target.value)} /></Flex>
        {!list ? <Spinner size="sm" /> : (
          <Box overflowX="auto">
            <Table size="sm" sx={{ "td, th": { fontSize: type.size.caption, py: 1.5 } }}>
              <Thead bg={neutral[50]}>
                <Tr><Th>Rep</Th><Th>Email</Th><Th>Cellphone</Th><Th>Home address</Th><Th isNumeric>Stores / day</Th><Th isNumeric>Dis-Chem</Th><Th isNumeric>Checkers</Th><Th isNumeric>Clicks</Th><Th>Login</Th><Th></Th></Tr>
              </Thead>
              <Tbody>
                {rows.map((r) => (
                  <Tr key={r.rep_name}>
                    <Td fontWeight={600}>{r.rep_name}</Td>
                    <Td>{r.email || <Text as="span" color={neutral[400]}>–</Text>}</Td>
                    <Td whiteSpace="nowrap">{r.cell || <Text as="span" color={neutral[400]}>–</Text>}</Td>
                    <Td maxW="380px" whiteSpace="normal">
                      {r.home_lat == null ? <Badge colorScheme="red">No home</Badge> : <>{r.home_label || "–"} {r.home_dummy && <Badge colorScheme="orange" ml={1}>stand-in</Badge>}</>}
                    </Td>
                    <Td isNumeric>{r.stores_per_day}</Td>
                    <Td isNumeric>{r.dis || "–"}</Td>
                    <Td isNumeric>{r.chk || "–"}</Td>
                    <Td isNumeric>{r.cli || "–"}</Td>
                    <Td>{r.userid ? <Badge colorScheme="green">{r.username || "yes"}</Badge> : <Badge>none</Badge>}</Td>
                    <Td>{canEdit && <Button size="xs" variant="outline" leftIcon={<FaHome />} onClick={() => { const h = r.home_dummy ? "" : r.home_label || ""; setEdit({ mode: "home", rep_name: r.rep_name, home_label: h, home0: h, lat: "", lng: "", stores_per_day: r.stores_per_day || 6, spd0: r.stores_per_day || 6, email: r.email || "", cell: r.cell || "" }) }}>Edit</Button>}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
            {rows.length === 0 && <Text fontSize={type.size.small} color={neutral[500]} mt={2}>Nothing to show.</Text>}
          </Box>
        )}
      </Panel>

      <Modal isOpen={!!edit} onClose={() => setEdit(null)} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{edit?.mode === "add" ? "Add rep" : edit?.rep_name}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {edit?.mode === "add" && <Input mb={3} placeholder="Rep name (as it should show everywhere)" value={edit?.rep_name || ""} onChange={(e) => setEdit({ ...edit, rep_name: e.target.value })} />}
            <Text fontSize={type.size.small} color={neutral[600]} mb={1}>Contact</Text>
            <HStack mb={3}>
              <Input type="email" placeholder="Email address" value={edit?.email || ""} onChange={(e) => setEdit({ ...edit, email: e.target.value })} />
              <Input type="tel" maxW="200px" placeholder="Cellphone" value={edit?.cell || ""} onChange={(e) => setEdit({ ...edit, cell: e.target.value })} />
            </HStack>
            <Text fontSize={type.size.small} color={neutral[600]} mb={2}>Home address (found on the map), or the coordinates. The route starts and ends here; saving re-plans the rep.</Text>
            <Input mb={3} placeholder="e.g. 12 Main Road, Constantia, Cape Town" value={edit?.home_label || ""} onChange={(e) => setEdit({ ...edit, home_label: e.target.value })} />
            <HStack mb={3}>
              <Input placeholder="Latitude (optional)" value={edit?.lat || ""} onChange={(e) => setEdit({ ...edit, lat: e.target.value })} />
              <Input placeholder="Longitude (optional)" value={edit?.lng || ""} onChange={(e) => setEdit({ ...edit, lng: e.target.value })} />
            </HStack>
            <Text fontSize={type.size.small} color={neutral[600]} mb={1}>Stores per day</Text>
            <NumberInput min={1} max={15} value={edit?.stores_per_day || 6} onChange={(v) => setEdit({ ...edit, stores_per_day: Number(v) })} maxW="120px"><NumberInputField /></NumberInput>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={2} onClick={() => setEdit(null)}>Cancel</Button>
            <Button colorScheme="teal" onClick={save} isLoading={busy}
              isDisabled={edit?.mode === "add" ? !edit?.rep_name || edit.rep_name.trim().length < 3 : false}>
              {edit?.mode === "add" ? "Add rep" : "Save"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  )
}

export default RepReps
